import { Box, Flex } from 'reflexbox';
import styled, { keyframes } from 'styled-components';

import { tokens } from '@treams/treams-ui';

const animation = keyframes`
    0% { opacity: 0; }
    100% { opacity: 1; }
`;

const CurrentBar = styled(Box)`
	margin-left: 4px;
	width: 2px;
	flex-shrink: 0;
	flex-grow: 0;
	border-radius: 1px;

	transition: 0.1s linear all;
	animation: 0.4s cubic-bezier(0, 1, 0, 1) ${animation};
`;

function Current({ value, recording }: { value: number; recording?: boolean }) {
	return (
		<CurrentBar
			style={{
				height: `${value * 20}px`,
				transform: `translateY(${10 - value * 10}px)`,
				background: recording ? tokens.error.icon : tokens.brand.icon,
			}}
		/>
	);
}

const BAR_WIDTH = 2;
const BAR_SPACING = 6;
const BAR_HEIGHT = 20;
const BAR_RADIUS = 1;

export default function VoiceMemoWaveForm(props: {
	bars: number[];
	current?: number;
	recording?: boolean;
	progress?: number;
}) {
	const dpi = window.devicePixelRatio;

	const width = props.bars.length * BAR_SPACING - (BAR_SPACING - BAR_WIDTH);

	return (
		<Flex width={width + (props.current === undefined ? 0 : BAR_SPACING)}>
			<Box flexGrow={0} flexShrink={0}>
				<canvas
					width={width * dpi}
					height={BAR_HEIGHT * dpi}
					style={{
						display: 'block',
						width,
						height: BAR_HEIGHT,
					}}
					ref={(ref) => {
						if (!ref) return;

						const ctx = ref.getContext('2d')!;
						ctx.save();
						ctx.scale(dpi, dpi);
						ctx.clearRect(0, 0, ref.width, ref.height);

						props.bars.forEach((amplitude, i) => {
							const scaled = Math.max(0.1, Math.round(amplitude * 10) / 10);
							ctx.beginPath();
							ctx.roundRect(
								BAR_SPACING * i,
								Math.floor((1 - scaled) * (BAR_HEIGHT / 2)),
								BAR_WIDTH,
								Math.ceil(scaled * BAR_HEIGHT),
								BAR_RADIUS,
							);

							if (props.recording) {
								ctx.fillStyle = tokens.error.icon;
							} else {
								ctx.fillStyle = tokens.brand.icon;
							}

							ctx.globalAlpha =
								props.progress !== undefined && props.progress <= i / props.bars.length ? 0.5 : 1;

							ctx.closePath();
							ctx.fill();
						});

						ctx.restore();
					}}
				/>
			</Box>
			{props.current !== undefined && (
				<Current key={props.bars.length} value={props.current} recording={props.recording} />
			)}
		</Flex>
	);
}
